<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton
                title="Payroll Advice"
                :showAddNew="false"
                :showBtn="true"
                @onClickCloseButton="navigateToListPage"
            />
        </div>

      <div class="my-2 px-2">
        <div class="d-flex justify-content-between">
          <div>
             <h6>Payment Category: {{ paymentModeData.payment_mode_name }}</h6>
             <h6>Total Payable: {{ paymentModeData.total_payable_amount }}</h6>
             <h6>Total Paid: {{ paymentModeData.total_paid_amount }}</h6>
             <h6>Total Due: {{ paymentModeData.total_due_amount }}</h6>
          </div>
          <div>
            <div class="mb-1 col-12">
              <label for="colFormLabel" class="col-form-label">Payment Date</label>
              <vField
                  v-model="formData.payment_date"
                  name="date"
                  id="date"
                  type="date"
                  class="form-control"
              />
            </div>
            <button
                style="min-width: 64px;"
                :disabled="loading"
                @click="storeData"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            >Save
            </button>
          </div>
        </div>
      </div>

      <div class="  px-2">
        <div class="row">
          <div class="col-12 col-sm-6 col-md-3 col-lg-3">
            <div class="mb-1">
              <label for="colFormLabel" class="col-form-label">Employee</label>
              <AsyncSelect
                  multiple
                  placeholder="Select Employee"
                  v-model="contactProfileId"
                  :api-service="fetchContactProfiles"
                  :additional-query="{ type: 'employee', status: 'active'}"
              />
            </div>
          </div>
          <div class="col-12 col-sm-6 col-md-1 col-lg-1 mt-2">
            <button
                style="min-width: 64px;"
                @click="getPayrollData"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            > Go
            </button>
          </div>
        </div>
      </div>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table table-bordered">
                <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Net Payable</th>
                  <th>Already Paid Amount</th>
                  <th>Paid Amount</th>
                  <th>Due Amount</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in payrollData" :key="i">
                  <td>{{ item.employee_name + '(' + item.serial_no + ')' }}</td>
                  <td class="text-right">{{ item.payable_amount }}</td>
                  <td class="text-right">{{ item.paid_amount }}</td>
                  <td>
                    <input
                        type="number"
                        v-model.number="item.due_amount"
                        @input="updatePayment(item)"
                        class="form-control "
                    />
                  </td>
                  <td>{{ getDueAmount(item) }}</td>
                </tr>
                </tbody>
              </table>
              <p v-if="payrollData && payrollData.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>

        <Loader v-if="loading"/>

    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import {inject, onMounted, reactive, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import { computed } from 'vue';
import figureFormatter from "@/services/utils/figureFormatter";
import payrollStatementPdf from '@/services/utils/pdf/payrollStatementPdf';
import handleContact from '@/services/modules/contact'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const contactProfileId = ref('')
let saveButtonLoader = ref(false)
const payrollData = ref([]);
const paymentModeData = ref('');
const params = ref({
    offset: 20,
});
const { commaFormat } = figureFormatter();
const token = computed(() => localStorage.getItem('token'));
const {
  getPayrollAdviceData,
  createPayrollAdvice
} = handleHrPayroll()
const { fetchContactProfiles } = handleContact()

const { exportToPDF } = payrollStatementPdf();
const formData = ref({
  payment_date: '',
  payroll_statement_id: route.params.payrollStatementId,
  payment_mode_id: route.params.paymentModeId,
  payments: []
})

const getPayrollData = async () => {
  let query = getQuery();
  let payrollStatementId = route.params.payrollStatementId;

  await getPayrollAdviceData(payrollStatementId, query).then(res => {
    if (!res.status){
      return
    }

    payrollData.value = res.data.data;
    paymentModeData.value = res.data.details;

    formData.value.payments = payrollData.value
          .filter(item => item.due_amount > 0)
          .map(item => ({
            payroll_payment_allocation_id: item.id,
            contact_profile_id: item.contact_profile_id,
            paid_amount: item.due_amount
          }))

  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const storeData = async () => {
  let query = `?company_id=${route.params.companyId}`;

  loading.value = true;
  if (!formData.value.payment_date) {
    showError('Please select a payment date.')
    loading.value = false;
    return
  }
  if (formData.value.payments.length === 0) {
    showError('Please enter at least one paid amount.')
    loading.value = false;
    return
  }

  createPayrollAdvice(formData.value, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        getPayrollData()
        showSuccess(res.message)
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
        loading.value = false
  })

}

const getDueAmount = (item) => {
  return item.payable_amount - (item.paid_amount + (item.due_amount || 0))
}

const updatePayment = (item) => {
  const maxAmount = item.payable_amount - item.paid_amount

  if (item.due_amount > maxAmount) {
    showError(`The Paid Amount cannot exceed the remaining payable amount (${maxAmount}). Please enter a valid amount.`)
    const existing = formData.value.payments.find(p => p.contact_profile_id === item.contact_profile_id)
    item.due_amount = existing ? maxAmount : 0
    existing.paid_amount = existing ? maxAmount : 0
    return
  }

  if (item.due_amount < 0) {
    showError(`Paid Amount cannot be negative.`);
    item.due_amount = 0;
    return;
  }

  const existing = formData.value.payments.find(p => p.contact_profile_id === item.contact_profile_id)
  if (existing) {
    existing.paid_amount = item.due_amount
  }
  if (! existing){
    formData.value.payments.push({
      contact_profile_id: item.contact_profile_id,
      paid_amount: item.due_amount
    })
  }
}

function navigateToListPage() {
  router.push({name: `payroll-process`, params: route.params, query: route.query});
}

function getQuery() {
  let query = `?company_id=${route.params.companyId}&payment_mode_id=${route.params.paymentModeId}`;
  if (contactProfileId.value && contactProfileId.value.length) {
    const ids = contactProfileId.value.map(item => item.id).join(',');
    query += `&contact_profile_id=${ids}`;
  }
  return query;
}

onMounted(() => {
    Promise.all([
      getPayrollData(),
    ])
})
</script>
